import type { WidgetProps } from '@rjsf/utils'
import { Mtds, PlusIcon } from 'prove-common'
import { useMemo } from 'react'

export const SelectPayloadSchemaWidget = ({
  disabled,
  id,
  onChange,
  options: { enumOptions: options },
  placeholder,
  required,
  value,
  registry
}: WidgetProps) => {
  const formContext = registry.formContext
  const contractId = formContext?.contractId
  const selectedValues = Array.isArray(value)
    ? new Set(value.map(String))
    : null
  const selected = options
    ?.filter((o) =>
      selectedValues
        ? selectedValues.has(String(o.value))
        : `${o.value}` === `${value}`
    )
    .map(({ label, value }) => ({ label, value: String(value) })) // Convert value to string for HTML compatibility

  const isAnySelectionNewCombination = options?.some(({ schema }) => {
    return selected?.some(
      (sel) =>
        sel.value === `${schema?.const}` &&
        schema?.['x-new-combination'] === true
    )
  })

  const { regularOptions, newCombinationOptions } = useMemo(() => {
    const regular = (options ?? []).filter(
      ({ schema }) => schema?.['x-new-combination'] !== true
    )
    const newCombos = (options ?? []).filter(
      ({ schema }) => schema?.['x-new-combination'] === true
    )
    return { regularOptions: regular, newCombinationOptions: newCombos }
  }, [options])

  const handleSelectedChange = (item: { label: string; value: string }) => {
    onChange(options?.find((o) => `${o.value}` === `${item?.value}`)?.value)
  }

  const openCreatePayloadSchema = () => {
    const url = `/contracts/${contractId}?tab=1&create=true`
    window.open(url, '_blank')
  }

  return (
    <Mtds.Field.Suggestion
      data-color={isAnySelectionNewCombination ? 'info' : undefined}
      selected={selected}
      onSelectedChange={([item]) => handleSelectedChange(item)}
    >
      <Mtds.Input
        disabled={disabled}
        id={id}
        placeholder={placeholder}
        required={required}
      />
      <button type='reset' aria-label='Fjern tekst' />
      <Mtds.Field.Datalist>
        {regularOptions.map(({ label, value }) => (
          <Mtds.Field.Option key={value} value={value}>
            {label}
          </Mtds.Field.Option>
        ))}

        {regularOptions.length > 0 && newCombinationOptions.length > 0 && (
          <div role='separator'>
            <Mtds.Divider data-gap={0} />
          </div>
        )}

        {newCombinationOptions.map(({ label, value }) => {
          return (
            <Mtds.Field.Option key={value} value={value} data-color={'info'}>
              <Mtds.Flex data-justify='space-between'>
                {label}
                <Mtds.Tag data-color={'info'} data-size='sm'>
                  Ny kombinasjon
                </Mtds.Tag>
              </Mtds.Flex>
            </Mtds.Field.Option>
          )
        })}

        <div role='separator'>
          <Mtds.Divider data-gap={0} />
        </div>

        <Mtds.Button
          className='w-full'
          data-variant='tertiary'
          onMouseDown={(event) => event.preventDefault()}
          onClick={openCreatePayloadSchema}
        >
          <PlusIcon />
          Opprett ny
        </Mtds.Button>
      </Mtds.Field.Datalist>
    </Mtds.Field.Suggestion>
  )
}
